import React, { useEffect, useMemo, useRef } from 'react';
import Flatpickr, { type DateTimePickerHandle } from 'react-flatpickr';
import ReactDOMServer from 'react-dom/server';
import moment, { type MomentInput } from 'moment';
import { cs } from '@bbkAdminUtils/css-utils';

import 'flatpickr/dist/themes/material_green.css';
import './date-range-picker.scss';

const arrow = (
  <path d="M.273.192a1 1 0 0 1 1.182 0L5 3.355 8.546.192a1 1 0 0 1 1.181 0 .62.62 0 0 1 0 1.026l-4.273 3.59a1 1 0 0 1-1.181 0l-4-3.59Q-.001.962 0 .705 0 .322.273.193" />
);
const prevArrow = (
  <svg
    width="10"
    height="5"
    viewBox="0 0 10 5"
    className="tw-translate-x-1 tw-rotate-90 tw-transform"
  >
    {arrow}
  </svg>
);
const nextArrow = (
  <svg width="10" height="5" viewBox="0 0 10 5" className="-tw-rotate-90">
    {arrow}
  </svg>
);
// Only includes the inputs that are included as part of the time picker

const DatePickerLabel: React.FC<React.PropsWithChildren> = ({ children }) => (
  <div className="bbk-date-picker-label-text tw-mb-0.5 tw-text-[0.625rem] tw-text-black/50">
    {children}
  </div>
);

type FlatpickrProps = React.ComponentProps<typeof Flatpickr>;
type FlatpickrOnChange = FlatpickrProps['onChange'];

type Props = {
  startDate: MomentInput;
  endDate: MomentInput;
  onRangeChange: (args: [Date, Date]) => unknown;
  autoOpen?: boolean;
  disabled?: boolean;
  loading?: boolean;
};

export const DateRangePicker: React.FC<React.PropsWithChildren<Props>> = (
  props
) => {
  const { startDate, endDate, onRangeChange, autoOpen, disabled, loading } =
    props;

  const startDateTZ = useMemo(() => {
    const startNoTZ = moment.utc(startDate).format('YYYY-MM-DD');
    return moment(startNoTZ).startOf('day').toDate();
  }, [startDate]);

  const endDateTZ = useMemo(() => {
    const endTZ = moment.utc(endDate).format('YYYY-MM-DD');
    return moment(endTZ).endOf('day').toDate();
  }, [endDate]);

  const onRangeChangeWithTransformation: Props['onRangeChange'] = ([
    start,
    end,
  ]) => {
    const startNoTZ = moment(start).format('YYYY-MM-DD');
    const startUTC = moment.utc(startNoTZ).startOf('day').toDate();
    const endNoTZ = moment(end).format('YYYY-MM-DD');
    const endUTC = moment.utc(endNoTZ).endOf('day').toDate();

    onRangeChange([startUTC, endUTC]);
  };

  const startInput = useRef<DateTimePickerHandle>(null);
  const endInput = useRef<DateTimePickerHandle>(null);

  useEffect(() => {
    if (autoOpen) {
      startInput.current?.flatpickr?.open();
    }
  }, [autoOpen]);

  const handleStartChange: FlatpickrOnChange = (dates) => {
    const date = dates[0] as Date;

    if (moment(date).isBefore(endDateTZ) || moment(date).isSame(endDateTZ)) {
      onRangeChangeWithTransformation([date, endDateTZ]);
    } else {
      const newEndDate = moment(date).add(1, 'day').toDate();
      const curEndInputRef = endInput.current;
      if (curEndInputRef) {
        curEndInputRef.flatpickr?.setDate(newEndDate);
        curEndInputRef.flatpickr?.input.focus();
        curEndInputRef.flatpickr?.open();
      }
      onRangeChangeWithTransformation([date, newEndDate]);
    }
  };

  const handleEndChange: FlatpickrOnChange = (dates) => {
    const date = dates[0] as Date;

    if (moment(date).isAfter(startDateTZ) || moment(date).isSame(startDateTZ)) {
      onRangeChangeWithTransformation([startDateTZ, date]);
    } else {
      const newStartDate = moment(date).subtract(1, 'day').toDate();
      const curStartInputRef = startInput.current;
      if (curStartInputRef) {
        curStartInputRef.flatpickr?.setDate(newStartDate);
        curStartInputRef.flatpickr?.input.focus();
        curStartInputRef.flatpickr?.open();
      }
      onRangeChangeWithTransformation([newStartDate, date]);
    }
  };

  return (
    <div
      className={cs('bbk-date-picker', {
        'bbk-date-picker-loading': loading,
      })}
    >
      <label className="bbk-date-picker-label">
        <DatePickerLabel>Start date:</DatePickerLabel>
        <Flatpickr
          id="flatpickr-first"
          autoComplete="off"
          placeholder="Start"
          disabled={disabled}
          value={startDateTZ}
          onChange={handleStartChange}
          options={{
            nextArrow: ReactDOMServer.renderToString(nextArrow),
            prevArrow: ReactDOMServer.renderToString(prevArrow),
            formatDate: (date) => moment(date).format('MM/DD/YY'),
          }}
          ref={startInput}
        />
      </label>
      <span />
      <label className="bbk-date-picker-label">
        <DatePickerLabel>End date:</DatePickerLabel>
        <Flatpickr
          id="flatpickr-second"
          autoComplete="off"
          placeholder="End"
          disabled={disabled}
          value={endDateTZ}
          onChange={handleEndChange}
          options={{
            nextArrow: ReactDOMServer.renderToString(nextArrow),
            prevArrow: ReactDOMServer.renderToString(prevArrow),
            formatDate: (date) => moment(date).format('MM/DD/YY'),
          }}
          ref={endInput}
        />
      </label>
    </div>
  );
};
